import { ReactNode, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import ChangeLine from '../../components/novarium/ChangeLine';
import InfoTip from '../../components/novarium/InfoTip';
import { NewBornFilterDTO, NewBornGranularity, NewBornKpisDTO, NewBornTimelinePointDTO } from '../../dto/newborn/NewBornDTO';
import { getNewBornBreakdown, getNewBornSummary } from '../../services/NewBornController';
import { percentVariation, pointsVariation, Variation, Verdict, waitVerdict } from '../ServicesStatistics/statisticsChange';
import { formatCount, formatPercent } from '../UrgencySummary/urgencySummaryFormat';
import useSectionData from '../UrgencySummary/useSectionData';
import NewBornBars from './NewBornBars';
import { filtersKey } from './newBornFilterModel';
import { RangeReference } from './newBornEvolutionModel';
import { periodTitleOf } from './newBornPeriodText';

interface NewBornPeriodSidebarProps {
    /** El período que se mira (el punto del gráfico); null con el panel cerrado. */
    point: NewBornTimelinePointDTO | null;
    granularity: NewBornGranularity;
    /** Los filtros aplicados de la pantalla: el panel pone las fechas del período. */
    filters: NewBornFilterDTO;
    /** Los totales de todo el rango: contra ellos se compara el período (no contra «el período anterior»). */
    reference: RangeReference;
    /** Mínimo de casos para escribir «mejor» o «peor» (el de los indicadores de la pantalla). */
    minCases: number;
    /** «Ver solo este período»: aplica sus fechas como período de la pantalla. */
    onViewPeriod: (from: string, to: string) => void;
    onHide: () => void;
}

/** Un porcentaje (en %) como fracción, que es lo que compara `pointsVariation`. */
const toRatio = (percent: number | null): number | null => (percent === null ? null : percent / 100);

interface KpiProps {
    label: string;
    hint?: string;
    value: ReactNode;
    /** Renglones debajo: «X de N» y la comparación con el promedio del rango. */
    details: ReactNode[];
}

function Kpi({ label, hint, value, details }: KpiProps) {
    const shown = details.filter(Boolean);
    return (
        <div className="nv-newborn-detail__kpi">
            <dt className="nv-newborn-detail__kpi-label">
                {label}
                {hint && <InfoTip text={hint} />}
            </dt>
            <dd className="nv-newborn-detail__kpi-value">{value}</dd>
            {shown.length > 0 && (
                <dd className="nv-newborn-detail__kpi-detail">
                    {shown.map((detail, index) => <span key={index}>{detail}</span>)}
                </dd>
            )}
        </div>
    );
}

interface SectionProps {
    id: string;
    title: string;
    /** Criterio de las categorías, a demanda. */
    hint?: string;
    children: ReactNode;
}

function Section({ id, title, hint, children }: SectionProps) {
    return (
        <section className="nv-newborn-detail__section" aria-labelledby={id}>
            <h3 className="nv-section-title nv-newborn-detail__heading" id={id}>
                {title}
                {hint && <InfoTip text={hint} />}
            </h3>
            {children}
        </section>
    );
}

/**
 * Detalle de un período del gráfico de evolución, en un panel lateral de Novarium (420 px, a todo el ancho en pantallas
 * angostas): los cuatro indicadores del período contra el promedio del rango entero, y cómo se reparten los nacidos vivos
 * por tipo de parto, destino, sexo, edad gestacional y peso. Las categorías sin nadie y las secciones sin datos no se
 * muestran; los nacidos muertos y los registros a revisar aparecen como nota solo si hay. Cada consulta (`/summary` y
 * `/breakdown`, con las fechas del período y los filtros de la pantalla) tiene su propia carga y su propio error con
 * «Reintentar»; los resultados quedan en un caché por período y filtros, y lo que deja de verse se cancela. Los nacimientos
 * del título se ven al instante (vienen del gráfico). Un período parcial no se compara en cantidad con el promedio de
 * períodos completos. Esc cierra el panel y el foco vuelve a quien lo abrió (lo resuelve quien abre el panel).
 * Pie: «Ver solo este período».
 */
export default function NewBornPeriodSidebar({
    point, granularity, filters, reference, minCases, onViewPeriod, onHide,
}: NewBornPeriodSidebarProps) {
    const intl = useIntl();
    // Mientras el panel se cierra se sigue mostrando lo último (sin parpadeo de «Cargando»)
    const lastShown = useRef<{ point: NewBornTimelinePointDTO; filters: NewBornFilterDTO } | null>(null);
    if (point) lastShown.current = { point, filters };
    const shown = point ? { point, filters } : lastShown.current;

    const periodFilters: NewBornFilterDTO | null = shown ? { ...shown.filters, from: shown.point.from, to: shown.point.to } : null;
    const baseKey = periodFilters ? filtersKey(periodFilters) : null;
    const required = (): NewBornFilterDTO => {
        if (!periodFilters) throw new Error('Panel sin período');
        return periodFilters;
    };
    const summary = useSectionData(baseKey === null ? null : `${baseKey}|summary`, (signal) => getNewBornSummary(required(), signal));
    const breakdown = useSectionData(baseKey === null ? null : `${baseKey}|breakdown`, (signal) => getNewBornBreakdown(required(), signal));

    if (!shown || !periodFilters) return null;
    const title = periodTitleOf(intl, shown.point, granularity);
    const kpis: NewBornKpisDTO | null = summary.data?.current ?? null;
    const pending = summary.status === 'failed' ? '—' : '…';

    const vs = (value: string) => intl.formatMessage({ id: 'NewBornVsRange' }, { value });
    const line = (variation: Variation | null, value: string, verdict: Verdict = null): ReactNode =>
        (variation ? <ChangeLine variation={variation} period={vs(value)} verdict={verdict} /> : undefined);
    const ofTotal = (count: number, base: number, messageId: string): string =>
        intl.formatMessage({ id: messageId }, { count: formatCount(intl, count), total: formatCount(intl, base) });

    /** Una tasa contra la del rango: variación en puntos y, con el mínimo de casos en las dos bases, veredicto (subir es peor). */
    const rate = (pct: number | null, refPct: number | null, base: number, refBase: number): ReactNode => {
        const variation = pointsVariation(intl, toRatio(pct), toRatio(refPct));
        const enough = base >= minCases && refBase >= minCases;
        return line(variation, formatPercent(intl, refPct), variation && enough ? waitVerdict(variation.trend) : null);
    };

    const averageBirths = reference.birthsPerPeriod;
    const birthsChange = !shown.point.partial && averageBirths !== null && averageBirths > 0
        ? percentVariation(shown.point.births, averageBirths) : null;

    const loadingDetail = (
        <span key="loading" className="nv-loading" role="status">
            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
            <span>{intl.formatMessage({ id: 'NewBornLoading' })}</span>
        </span>
    );

    const data = breakdown.data;
    const liveBirths = data?.liveBirths ?? 0;
    const section = (id: string, titleId: string, hintId: string | undefined, rows: { key: string; label: string; count: number }[]) => (
        rows.some((row) => row.count > 0) && liveBirths > 0 ? (
            <Section
                key={id}
                id={id}
                title={intl.formatMessage({ id: titleId })}
                hint={hintId ? intl.formatMessage({ id: hintId }) : undefined}
            >
                <NewBornBars rows={rows} total={liveBirths} ariaLabelledBy={id} />
            </Section>
        ) : null
    );

    return (
        <Sidebar
            visible={point !== null}
            position="right"
            onHide={onHide}
            showCloseIcon={false}
            blockScroll
            className="nv-scope nv-drawer nv-newborn-detail"
            aria-labelledby="nv-newborn-detail-title"
        >
            <div className="nv-drawer-layout">
                <header className="nv-drawer-head">
                    <h2 className="nv-modal-title nv-newborn-detail__title" id="nv-newborn-detail-title">{title}</h2>
                    <Button
                        type="button"
                        text
                        className="nv-icon nv-tip"
                        icon="pi pi-times"
                        aria-label={intl.formatMessage({ id: 'NewBornDetailClose' })}
                        data-pr-tooltip={intl.formatMessage({ id: 'NewBornDetailClose' })}
                        onClick={onHide}
                    />
                </header>

                <div className="nv-drawer-body">
                    <section className="nv-newborn-detail__section" aria-label={intl.formatMessage({ id: 'NewBornKpisLabel' })}>
                        <dl className="nv-newborn-detail__kpis">
                            <Kpi
                                label={intl.formatMessage({ id: 'NewBornKpiBirths' })}
                                value={formatCount(intl, shown.point.births)}
                                details={[line(birthsChange, formatCount(intl, Math.round(averageBirths ?? 0)))]}
                            />
                            <Kpi
                                label={intl.formatMessage({ id: 'NewBornKpiCesarean' })}
                                value={kpis === null ? pending : formatPercent(intl, kpis.cesareanPct)}
                                details={kpis === null ? [summary.status === 'loading' ? loadingDetail : undefined] : [
                                    kpis.cesareanBase > 0 ? ofTotal(kpis.cesareans, kpis.cesareanBase, 'NewBornKpiOfDeliveries') : undefined,
                                    rate(kpis.cesareanPct, reference.cesareanPct, kpis.cesareanBase, reference.cesareanBase),
                                ]}
                            />
                            <Kpi
                                label={intl.formatMessage({ id: 'NewBornKpiPremature' })}
                                value={kpis === null ? pending : formatPercent(intl, kpis.pretermPct)}
                                details={kpis === null ? [] : [
                                    kpis.pretermBase > 0 ? ofTotal(kpis.preterm, kpis.pretermBase, 'NewBornKpiOf') : undefined,
                                    rate(kpis.pretermPct, reference.pretermPct, kpis.pretermBase, reference.pretermBase),
                                ]}
                            />
                            <Kpi
                                label={intl.formatMessage({ id: 'NewBornKpiLowWeight' })}
                                value={kpis === null ? pending : formatPercent(intl, kpis.lowWeightPct)}
                                details={kpis === null ? [] : [
                                    kpis.lowWeightBase > 0 ? ofTotal(kpis.lowWeight, kpis.lowWeightBase, 'NewBornKpiOf') : undefined,
                                    rate(kpis.lowWeightPct, reference.lowWeightPct, kpis.lowWeightBase, reference.lowWeightBase),
                                ]}
                            />
                        </dl>
                        {summary.status === 'failed' && (
                            <div className="nv-newborn-failed" role="status">
                                <p className="nv-note">
                                    <i className="pi pi-info-circle" aria-hidden="true" />
                                    {intl.formatMessage({ id: 'NewBornDetailKpisFailed' })}
                                </p>
                                <Button type="button" link label={intl.formatMessage({ id: 'NewBornRetry' })} onClick={summary.retry} />
                            </div>
                        )}
                    </section>

                    {breakdown.status === 'loading' && (
                        <span className="nv-loading nv-newborn-detail__loading" role="status">
                            <ProgressSpinner strokeWidth="4" style={{ width: 16, height: 16 }} />
                            <span>{intl.formatMessage({ id: 'NewBornLoading' })}</span>
                        </span>
                    )}
                    {breakdown.status === 'failed' && (
                        <div className="nv-newborn-failed" role="status">
                            <p className="nv-note">
                                <i className="pi pi-info-circle" aria-hidden="true" />
                                {intl.formatMessage({ id: 'NewBornDetailBreakdownFailed' })}
                            </p>
                            <Button type="button" link label={intl.formatMessage({ id: 'NewBornRetry' })} onClick={breakdown.retry} />
                        </div>
                    )}
                    {data && (
                        <>
                            {section('nv-newborn-detail-delivery', 'NewBornDetailDelivery', undefined,
                                data.deliveryTypes.map((row) => ({ key: row.code ?? 'none', label: row.name, count: row.count })))}
                            {section('nv-newborn-detail-placement', 'NewBornDetailPlacement', undefined,
                                data.placements.map((row) => ({ key: String(row.id ?? 'none'), label: row.name, count: row.count })))}
                            {section('nv-newborn-detail-sex', 'NewBornDetailSex', undefined,
                                data.sexes.map((row) => ({ key: String(row.id ?? 'none'), label: row.name, count: row.count })))}
                            {section('nv-newborn-detail-gestation', 'NewBornDetailGestation', 'NewBornDetailBandsHint',
                                data.gestationalBands.map((row) => ({ key: row.key, label: row.name, count: row.count })))}
                            {section('nv-newborn-detail-weight', 'NewBornDetailWeight', 'NewBornDetailBandsHint',
                                data.weightBands.map((row) => ({ key: row.key, label: row.name, count: row.count })))}
                            {data.stillbirths > 0 && (
                                <p className="nv-note" role="status">
                                    <i className="pi pi-info-circle" aria-hidden="true" />
                                    <span>
                                        {intl.formatMessage({ id: 'NewBornNoteStillborn' }, {
                                            count: data.stillbirths, countText: formatCount(intl, data.stillbirths),
                                        })}
                                    </span>
                                    <InfoTip text={intl.formatMessage({ id: 'NewBornNoteStillbornHint' })} />
                                </p>
                            )}
                            {data.needsReview > 0 && (
                                <p className="nv-note" role="status">
                                    <i className="pi pi-info-circle" aria-hidden="true" />
                                    <span>
                                        {intl.formatMessage({ id: 'NewBornNoteReview' }, {
                                            count: data.needsReview, countText: formatCount(intl, data.needsReview),
                                        })}
                                    </span>
                                    <InfoTip text={intl.formatMessage({ id: 'NewBornNoteReviewHint' })} />
                                </p>
                            )}
                        </>
                    )}
                </div>

                <footer className="nv-drawer-foot">
                    <Button
                        type="button"
                        className="buttonMain3"
                        label={intl.formatMessage({ id: 'NewBornDetailViewOnly' })}
                        onClick={() => onViewPeriod(shown.point.from, shown.point.to)}
                    />
                </footer>
            </div>
        </Sidebar>
    );
}
